
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { percentile } from '@/functions';
import LineChart from './LineChart';
import ChartMixin from './ChartMixin.vue';

const PERCENTILE_STEPS = [0, 10, 25, 50, 75, 90, 100] as const;
const BAND_COLOR_0_100 = 'rgba(102, 102, 255, 0.2)';
const BAND_COLOR_10_90 = 'rgba(102, 102, 255, 0.3)';
const BAND_COLOR_25_75 = 'rgba(102, 102, 255, 0.4)';
const MEDIAN_LINE_COLOR = 'rgba(0, 0, 255, 0.6)';

@Component({
  components: {
    LineChart,
  },
})
export default class EnChosuiBandChart extends Mixins(ChartMixin) {
  private chartOptions = null;

  get rerender() {
    return this['$store'].getters.rerender;
  }

  set rerender(value) {
    this['$store'].commit('setRerender', value);
  }

  get gridPitch() {
    return this['$store'].getters.enGridPitch;
  }

  get minHScale() {
    return this['$store'].getters.minHScale === '自動' ? this['$store'].getters.minHAutoScale : this['$store'].getters.minHScale;
  }

  get maxHScale() {
    return this['$store'].getters.maxHScale === '自動' ? this['$store'].getters.maxHAutoScale : this['$store'].getters.maxHScale;
  }

  get targetTime() {
    return this['$store'].getters.enTargetTime;
  }

  get yskSpan() {
    return this['$store'].getters.enYskSpan;
  }

  get autoHStep() {
    const { stationsMap } = this['$store'].getters;
    return stationsMap[this.stationId].scales.autoHStep;
  }

  mounted() {
    this.updateResult();
  }

  getOneFromResult(result, key) {
    return result.map((obj) => {
      const keyValue = obj[key] ?? null;
      return keyValue <= -999 ? null : keyValue;
    });
  }

  buildLimitLinesDatasets(dataTimeArray: string[]) {
    const { stationsMap } = this['$store'].getters;
    return stationsMap[this.stationId].limitLines.map((line) => {
      const data: any = [];
      line.details.forEach((detail) => {
        const startDate = new Date(this['$store'].getters.targetTime.replace(/-/g, '/'));
        const endDate = new Date(this['$store'].getters.targetTime.replace(/-/g, '/'));

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        startDate.setMonth(detail.start.split('/')[0] - 1);
        startDate.setDate(detail.start.split('/')[1]);
        endDate.setMonth(detail.end.split('/')[0] - 1);
        endDate.setDate(detail.end.split('/')[1]);
        if (startDate > endDate) {
          endDate.setFullYear(endDate.getFullYear() + 1);
        }

        dataTimeArray.slice(0, 7 + this.yskSpan).forEach((dataTime, index) => {
          const targetData = new Date(dataTime);
          if (startDate < targetData && targetData < endDate) {
            data.push(detail.value);
          }

          let updateValue = false;
          if (startDate <= targetData && targetData <= endDate) {
            updateValue = true;
            data[index] = detail.value;
          }
          if (!updateValue) {
            startDate.setFullYear(startDate.getFullYear() - 1);
            endDate.setFullYear(endDate.getFullYear() - 1);
            if (startDate <= targetData && targetData <= endDate) {
              updateValue = true;
              data[index] = detail.value;
            }
            startDate.setFullYear(startDate.getFullYear() + 1);
            endDate.setFullYear(endDate.getFullYear() + 1);
          }
          if (!updateValue) {
            startDate.setFullYear(startDate.getFullYear() + 1);
            endDate.setFullYear(endDate.getFullYear() + 1);
            if (startDate <= targetData && targetData <= endDate) {
              data[index] = detail.value;
            }
            startDate.setFullYear(startDate.getFullYear() - 1);
            endDate.setFullYear(endDate.getFullYear() - 1);
          }
        });
      });
      let showUsageGuide = false;
      data.forEach((val) => {
        if (val && this.minHScale <= val && val <= this.maxHScale) {
          showUsageGuide = true;
        }
      });
      return {
        label: line.name,
        data,
        fill: false,
        pointRadius: 0,
        borderWidth: 2,
        borderColor: line.color,
        borderDash: [5, 3],
        showUsageGuide,
        style: `border-top-color: ${line.color};border-top-style: dotted;`,
      };
    });
  }

  @Watch('tab')
  @Watch('yskSpan')
  @Watch('result')
  @Watch('rerender')
  public async updateResult() {
    if (!this.result || this.result.length === 0) return;

    const { type } = this['$store'].getters;
    const key = 'wlevel';
    const labelPrefix = Number(type) === 7 ? '貯水位' : '水位';
    const bottomColor = Number(type) === 7 ? '#080' : '#0f0';
    const numTimestamps = this.result[0].length;

    const percentileArrays: Record<number, (number | null)[]> = {
      0: [], 10: [], 25: [], 50: [], 75: [], 90: [], 100: [],
    };
    for (let timeIndex = 0; timeIndex < numTimestamps; timeIndex += 1) {
      const sortedValues = this.result
        .map((member) => {
          const v = member[timeIndex]?.[key] ?? null;
          return v == null || v <= -999 ? null : v;
        })
        .filter((v): v is number => v != null)
        .sort((a, b) => a - b);
      PERCENTILE_STEPS.forEach((p) => {
        percentileArrays[p].push(sortedValues.length > 0 ? percentile(sortedValues, p) : null);
      });
    }

    const spanLen = 7 + this.yskSpan;
    const [p0, p10, p25, p50, p75, p90, p100] = PERCENTILE_STEPS.map((p) => percentileArrays[p].slice(0, spanLen));

    const top = this.getOneFromResult(this.result[0], key).slice(0, spanLen);
    const middle = this.getOneFromResult(this.result[Math.floor(this.result.length / 2)], key).slice(0, spanLen);
    const bottom = this.getOneFromResult(this.result[this.result.length - 1], key).slice(0, spanLen);

    const dataTimeArray = this.result[0].map((obj) => obj.datetime);
    const validP100 = p100.filter((v): v is number => v != null);
    const validP0 = p0.filter((v): v is number => v != null);
    let maxNum = validP100.length > 0 ? Math.max(...validP100) : 0;
    let minNum = validP0.length > 0 ? Math.min(...validP0) : 0;
    const validTop = top.filter((v: number | null): v is number => v != null) as number[];
    const validBottom = bottom.filter((v: number | null): v is number => v != null) as number[];
    if (validTop.length > 0) maxNum = Math.max(maxNum, ...validTop);
    if (validBottom.length > 0) minNum = Math.min(minNum, ...validBottom);

    this['$store'].commit('setMaxHAutoScale', Math.ceil(maxNum / this.autoHStep) * this.autoHStep);
    this['$store'].commit('setMinHAutoScale', Math.floor(minNum / this.autoHStep) * this.autoHStep);
    const { maxHAutoScale, minHAutoScale } = this['$store'].getters;
    if (Math.abs(maxHAutoScale - minHAutoScale) <= this.autoHStep) {
      this['$store'].commit('setMaxHAutoScale', maxHAutoScale + this.autoHStep);
    }

    const limitDatasets = this.buildLimitLinesDatasets(dataTimeArray);

    this.chartData = {
      labels: dataTimeArray.slice(0, spanLen),
      datasets: [
        // 0-100% 帯 (P100 → P0) — 凡例はサイドバーで表示
        {
          label: 'P100', data: p100, fill: false, pointRadius: 0, borderWidth: 0, borderColor: 'transparent', notShowLable: true, hideInTooltip: true, showUsageGuide: false,
        },
        {
          label: '0-100%', data: p0, fill: '-1', pointRadius: 0, borderWidth: 0, borderColor: 'transparent', backgroundColor: BAND_COLOR_0_100, notShowLable: true, hideInTooltip: true, showUsageGuide: false,
        },
        // 10-90% 帯 (P90 → P10)
        {
          label: 'P90', data: p90, fill: false, pointRadius: 0, borderWidth: 0, borderColor: 'transparent', notShowLable: true, hideInTooltip: true, showUsageGuide: false,
        },
        {
          label: '10-90%', data: p10, fill: '-1', pointRadius: 0, borderWidth: 0, borderColor: 'transparent', backgroundColor: BAND_COLOR_10_90, notShowLable: true, hideInTooltip: true, showUsageGuide: false,
        },
        // 25-75% 帯 (P75 → P25)
        {
          label: 'P75', data: p75, fill: false, pointRadius: 0, borderWidth: 0, borderColor: 'transparent', notShowLable: true, hideInTooltip: true, showUsageGuide: false,
        },
        {
          label: '25-75%', data: p25, fill: '-1', pointRadius: 0, borderWidth: 0, borderColor: 'transparent', backgroundColor: BAND_COLOR_25_75, notShowLable: true, hideInTooltip: true, showUsageGuide: false,
        },
        // 中央値 (P50 ライン)
        {
          label: '中央値', data: p50, fill: false, pointRadius: 0, borderWidth: 1, borderColor: MEDIAN_LINE_COLOR, notShowLable: true, showUsageGuide: false,
        },
        // 上中下位（既存の3本ライン、凡例に表示）
        {
          label: `上位${labelPrefix}`, data: top, fill: false, pointRadius: 0, borderWidth: 2, borderColor: '#f00', style: 'border-top-color: #f00;', showUsageGuide: true, fixed: true,
        },
        {
          label: `中位${labelPrefix}`, data: middle, fill: false, pointRadius: 0, borderWidth: 2, borderColor: '#00f', style: 'border-top-color: #00f;', showUsageGuide: true, fixed: true,
        },
        {
          label: `下位${labelPrefix}`, data: bottom, fill: false, pointRadius: 0, borderWidth: 2, borderColor: bottomColor, style: `border-top-color: ${bottomColor};`, showUsageGuide: true, fixed: true,
        },
        ...limitDatasets,
      ],
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        filter: (tooltipItem, chartData) => !chartData.datasets[tooltipItem.datasetIndex].hideInTooltip,
        callbacks: {
          label: (tooltipItem, obj) => `${obj.datasets[tooltipItem.datasetIndex].label}:${Number(tooltipItem.value).toFixed(2)}`,
        },
      },
      legend: {
        labels: {
          filter: () => false,
        },
      },
      annotation: {
        annotations: [{
          type: 'line',
          scaleID: 'x-axis-0',
          value: this.targetTime,
          borderWidth: 2,
          borderColor: 'gray',
        }],
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: { hour: 'HH:mm' },
              stepSize: this.gridPitch,
            },
            ticks: { fontSize: 14, fontColor: 'black' },
            gridLines: { color: '#999' },
          },
          {
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: { day: 'MM/DD' },
            },
            gridLines: {
              drawTicks: false,
              drawBorder: false,
              drawOnChartArea: false,
              zeroLineColor: 'rgba(0, 0, 0, 0.1)',
              color: '#999',
            },
            ticks: { fontSize: 14, fontColor: 'black' },
          },
        ],
        yAxes: [{
          afterFit(scaleInstance) {
            // eslint-disable-next-line no-param-reassign
            scaleInstance.width = 40;
          },
          afterBuildTicks: (scale) => {
            let num = this.maxHScale;
            // eslint-disable-next-line no-param-reassign
            scale.ticks = [num];
            const step = (this.maxHScale - this.minHScale) / 10;
            while (scale.ticks.length < 11) {
              num -= step;
              // eslint-disable-next-line no-param-reassign
              scale.ticks.push(num);
            }
          },
          gridLines: { color: '#999' },
          ticks: {
            callback: (value: any) => value.toFixed(1),
            stepSize: 0.1,
            min: this.minHScale,
            max: this.maxHScale,
            fontSize: 14,
            fontColor: 'black',
          },
        },
        {
          position: 'right',
          display: true,
          gridLines: { color: '#999' },
          ticks: {
            callback() { return ''; },
            fontSize: 14,
            fontColor: 'black',
          },
        },
        ],
      },
    };
  }
}
